import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Languages } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import SegmentedToggle from "../../../components/SegmentedToggle";
import { LANGUAGE_OPTIONS } from "../../localization/languages";
import { OrganizationKind } from "../enums/OrganizationKind";
import { VOCABULARY_LOCALES, VOCABULARY_SLOTS } from "../enums/VocabularySlot";
import { selectOrganization, selectVocabularyError } from "../organization-slice";
import { updateOrganizationProfile } from "../slice-extensions/organization-slice-extension";
import { toVocabularyLocale } from "../utils/vocabularyVariables";
import { DEFAULT_VOCABULARY } from "../utils/defaultVocabulary";
import VocabularyTermRow from "./VocabularyTermRow";

const localeLabel = (code: string): string =>
  LANGUAGE_OPTIONS.find((option) => option.code === code)?.label ?? code;

/**
 * Organization settings card (Owner/Admin): the kind of school, the school
 * language and the words the app uses for each concept, per locale.
 */
const OrganizationVocabularyCard: React.FC = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const organization = useAppSelector(selectOrganization);
  const error = useAppSelector(selectVocabularyError);
  // The editor offers only the locales an organization can rename; an Arabic UI edits English.
  const [locale, setLocale] = useState(() => {
    const reading = toVocabularyLocale(i18n.language);
    return VOCABULARY_LOCALES.includes(reading) ? reading : "en";
  });

  if (!organization) return null;

  const updateProfile = (change: { kind?: OrganizationKind; languageCode?: string }) =>
    dispatch(updateOrganizationProfile({ organizationId: organization.id, name: organization.name, ...change }));

  return (
    <div className="with-border rounded-2xl p-4 lg:p-6">
      <div className="mb-4 flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-95 text-primary-50 dark:bg-primary-30 dark:text-primary-80">
          <Languages size={20} strokeWidth={2} />
        </span>
        <div className="min-w-0">
          <h2 className="text-lg font-semibold leading-tight">{t("organization.vocabulary.title")}</h2>
          <p className="text-sm text-grey-50 dark:text-grey-70 mt-0.5">{t("organization.vocabulary.intro")}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div>
          <label htmlFor="organization_kind" className="mb-1 block text-sm font-medium text-grey-30 dark:text-white">{t("organization.vocabulary.kind")}</label>
          <select
            id="organization_kind"
            className="input w-full"
            value={organization.kind}
            onChange={(e) => updateProfile({ kind: e.target.value as OrganizationKind })}
          >
            <option value={OrganizationKind.Default}>{t("organization.vocabulary.kindDefault")}</option>
            <option value={OrganizationKind.Fahrschule}>{t("organization.vocabulary.kindFahrschule")}</option>
          </select>
        </div>
        <div>
          <label htmlFor="organization_language" className="mb-1 block text-sm font-medium text-grey-30 dark:text-white">{t("organization.vocabulary.language")}</label>
          <select
            id="organization_language"
            className="input w-full"
            value={organization.languageCode}
            onChange={(e) => updateProfile({ languageCode: e.target.value })}
          >
            {LANGUAGE_OPTIONS.map((option) => (
              <option key={option.code} value={option.code}>{option.label}</option>
            ))}
          </select>
          <p className="mt-1 text-xs text-grey-50 dark:text-grey-70">{t("organization.vocabulary.languageHint")}</p>
        </div>
      </div>

      <div className="mt-6 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <SegmentedToggle
          className="max-w-full overflow-x-auto [&>button]:shrink-0"
          value={locale}
          onChange={setLocale}
          options={VOCABULARY_LOCALES.map((code) => ({ value: code, label: localeLabel(code) }))}
        />
        <div className="hidden gap-4 text-xs text-grey-50 dark:text-grey-70 lg:flex">
          <span>{t("organization.vocabulary.singular")}</span>
          <span>{t("organization.vocabulary.plural")}</span>
        </div>
      </div>

      <div className="mt-3">
        {VOCABULARY_SLOTS.map((slot) => (
          <VocabularyTermRow
            key={`${locale}-${slot}`}
            organizationId={organization.id}
            locale={locale}
            slot={slot}
            term={organization.vocabulary[locale]?.[slot] ?? { ...DEFAULT_VOCABULARY[locale][slot], isOverridden: false }}
          />
        ))}
      </div>

      {error && <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error.message}</p>}
    </div>
  );
};

export default OrganizationVocabularyCard;
